import { MouseEvent, ReactNode } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Expand from "../../../icons/Expand";
import Pencil from "../../../icons/Pencil";
import Delete from "../../../icons/Delete";
import Send from "../../../icons/Send";
import Times from "../../../icons/Times";
import { createConditionLink } from "../../utils/conditions";
import StatusBadge from "../../components/StatusBadge";
import { TableCell, TableHeadCell } from "../../components/TableCell";
import ConditionNameLink from "./ConditionNameLink";
import {
  ConditionGroupRow,
  ConditionWithGroups,
  formatGroupTimestamp,
  formatNeedByDate,
  getStatusPresentation,
  getStatusText,
  getWorkOrderLinkLabel,
} from "./statusState";

const ConditionBlock = styled(Box)(() => ({
  border: "1px solid #e0e0e0",
  borderRadius: "4px",
  overflow: "hidden",
  backgroundColor: "#fff",
  minWidth: 0,
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  backgroundColor: "#fafafa",
  cursor: "pointer",
}));

type CommittedConditionGroupsProps = {
  conditionGroups: ConditionWithGroups[];
  selectedIds: number[];
  areAllSelected: boolean;
  onToggle: (conditionId: number) => void;
  onToggleAll: () => void;
  isReadOnly: boolean;
  /** Condition whose spec is being modified; every other condition is locked. */
  activeModifyConditionId: number | null;
  modificationStatus: string | null;
  isCommitLoading: boolean;
  onModifyCondition: (conditionId: number) => void;
  onCancelModifyCondition: (conditionId: number) => void;
  onResubmitCondition: (conditionId: number) => void;
  onDeleteSubmission: (groupId: string) => void;
};

const BASE_HEADERS = [
  "Group ID",
  "Submitted",
  "Priority",
  "Location",
  "Need By Date",
  "Request Note",
  "Status",
];

/** URL search param that persists which committed condition row is expanded. */
const EXPANDED_CONDITION_PARAM = "expanded-condition";

/**
 * True iff a work order is a real (non-condition) work order that should get
 * its own column (e.g. "cell", "electrolyte").
 */
const isWorkOrder = (workOrder: ConditionWorkOrdersResponse): boolean =>
  workOrder.item_type?.toLowerCase() !== "condition";

const isCanceledStatus = (status?: string | null): boolean => {
  const normalized = (status ?? "").trim().toLowerCase();
  return normalized === "canceled" || normalized === "cancelled";
};

/**
 * Title-case a work order `item_type` for its column header, appending
 * "Work Order" (e.g. "cell" -> "Cell Work Order").
 */
const formatWorkOrderTypeHeader = (itemType: string): string => {
  const normalized = itemType.trim();
  if (!normalized) {
    return "Work Order";
  }
  const titled = normalized
    .split(/[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return `${titled} Work Order`;
};

/**
 * Distinct, stably-ordered work order `item_type`s across a condition's group
 * rows. Each becomes its own column so every work order type gets a dedicated
 * cell (Jira link + status badge).
 */
const getWorkOrderTypes = (rows: ConditionGroupRow[]): string[] => {
  const seen = new Set<string>();
  const types: string[] = [];
  rows.forEach(({ workOrders }) => {
    workOrders.forEach((workOrder) => {
      if (!isWorkOrder(workOrder)) {
        return;
      }
      const itemType = workOrder.item_type ?? "";
      if (!seen.has(itemType)) {
        seen.add(itemType);
        types.push(itemType);
      }
    });
  });
  return types;
};

/**
 * True iff at least one of the condition's work orders in this group is not
 * cancelled — i.e. the group can still be cancelled (and modified).
 */
const hasActiveWorkOrder = (row: ConditionGroupRow): boolean =>
  row.workOrders.some(
    (workOrder) => isWorkOrder(workOrder) && !isCanceledStatus(workOrder.status)
  );

/**
 * True iff the condition has work orders in this group and every one of them
 * is cancelled. (A group with no work orders yet is still pending, not
 * cancelled.)
 */
const isRowFullyCanceled = (row: ConditionGroupRow): boolean => {
  const workOrders = row.workOrders.filter(isWorkOrder);
  return (
    workOrders.length > 0 &&
    workOrders.every((workOrder) => isCanceledStatus(workOrder.status))
  );
};

/**
 * Whether a condition can be modified: either it has an active work order to
 * change in place, or every group is fully cancelled, in which case
 * resubmitting recreates its work orders in a new group.
 */
const getModifyAvailability = (
  rows: ConditionGroupRow[]
): { canModify: boolean; title: string } => {
  if (rows.some(hasActiveWorkOrder)) {
    return { canModify: true, title: "Modify condition" };
  }
  if (rows.length > 0 && rows.every(isRowFullyCanceled)) {
    return {
      canModify: true,
      title: "Modify and resubmit as a new work order group",
    };
  }
  return { canModify: false, title: "No active work orders to modify" };
};

const formatModificationStatus = (status: string | null): string => {
  switch (status) {
    case "queued":
      return "Queued";
    case "failed":
      return "Failed";
    default:
      return "Modifying";
  }
};

/** Icon button for the condition header that doesn't toggle the collapse. */
const HeaderAction = ({
  title,
  disabled,
  onClick,
  color,
  children,
}: {
  title: string;
  disabled: boolean;
  onClick: () => void;
  color?: string;
  children: ReactNode;
}) => (
  <Tooltip title={title} arrow>
    <span onClick={(event: MouseEvent) => event.stopPropagation()}>
      <IconButton
        size="small"
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        sx={color ? { color } : undefined}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const CommittedConditionGroups = ({
  conditionGroups,
  selectedIds,
  areAllSelected,
  onToggle,
  onToggleAll,
  isReadOnly,
  activeModifyConditionId,
  modificationStatus,
  isCommitLoading,
  onModifyCondition,
  onCancelModifyCondition,
  onResubmitCondition,
  onDeleteSubmission,
}: CommittedConditionGroupsProps) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const expandedConditionId = searchParams.get(EXPANDED_CONDITION_PARAM);
  const isAnyConditionModifying = activeModifyConditionId !== null;
  const isResubmissionInFlight = modificationStatus === "queued";

  const toggleExpanded = (conditionId: number) => {
    const next = new URLSearchParams(searchParams);
    if (next.get(EXPANDED_CONDITION_PARAM) === String(conditionId)) {
      next.delete(EXPANDED_CONDITION_PARAM);
    } else {
      next.set(EXPANDED_CONDITION_PARAM, String(conditionId));
    }
    setSearchParams(next, { replace: true });
  };

  const visibleConditionIds = conditionGroups.map(
    ({ condition }) => condition.cell_condition_id
  );
  const someSelected = visibleConditionIds.some((id) =>
    selectedIds.includes(id)
  );

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          paddingX: 1,
        }}
      >
        <Checkbox
          color="secondary"
          checked={areAllSelected && visibleConditionIds.length > 0}
          indeterminate={!areAllSelected && someSelected}
          onChange={onToggleAll}
          disabled={visibleConditionIds.length === 0 || isReadOnly}
          inputProps={{ "aria-label": "Select all committed conditions" }}
          sx={{ padding: 0 }}
        />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Select all
        </Typography>
      </Box>
      {conditionGroups.map(({ condition, rows }) => {
        const conditionId = condition.cell_condition_id;
        const isExpanded = expandedConditionId === String(conditionId);
        const isSelected = selectedIds.includes(conditionId);
        const workOrderTypes = getWorkOrderTypes(rows);
        const isConditionModifying = activeModifyConditionId === conditionId;
        const { canModify, title: modifyTitle } = getModifyAvailability(rows);
        return (
          <ConditionBlock key={conditionId}>
            <HeaderRow onClick={() => toggleExpanded(conditionId)}>
              <Checkbox
                color="secondary"
                checked={isSelected}
                disabled={isReadOnly}
                onClick={(event) => event.stopPropagation()}
                onChange={() => onToggle(conditionId)}
                inputProps={{
                  "aria-label": `Select ${condition.name}`,
                }}
                sx={{ padding: 0 }}
              />
              <IconButton
                size="small"
                aria-label={
                  isExpanded ? "Collapse condition" : "Expand condition"
                }
                onClick={(event) => {
                  event.stopPropagation();
                  toggleExpanded(conditionId);
                }}
              >
                <Expand
                  style={isExpanded ? { transform: "rotate(90deg)" } : {}}
                />
              </IconButton>
              {isConditionModifying && (
                <StatusBadge
                  label={formatModificationStatus(modificationStatus)}
                  colors={getStatusPresentation("Modifying")}
                />
              )}
              <ConditionNameLink
                name={condition.name}
                to={createConditionLink(
                  location.pathname,
                  searchParams,
                  conditionId
                )}
                hasViolations={false}
                onClick={(event) => event.stopPropagation()}
              />
              <Box sx={{ marginLeft: "auto", display: "flex", gap: 0.5 }}>
                {isConditionModifying ? (
                  <>
                    <HeaderAction
                      title="Resubmit"
                      onClick={() => onResubmitCondition(conditionId)}
                      disabled={isCommitLoading || isResubmissionInFlight}
                      color="#000"
                    >
                      <Send fontSize="small" />
                    </HeaderAction>
                    <HeaderAction
                      title="Cancel modification"
                      onClick={() => onCancelModifyCondition(conditionId)}
                      disabled={isCommitLoading || isResubmissionInFlight}
                      color="#d32f2f"
                    >
                      <Times fontSize="small" />
                    </HeaderAction>
                  </>
                ) : (
                  <HeaderAction
                    title={modifyTitle}
                    onClick={() => onModifyCondition(conditionId)}
                    disabled={
                      isAnyConditionModifying || isCommitLoading || !canModify
                    }
                  >
                    <Pencil fontSize="small" />
                  </HeaderAction>
                )}
              </Box>
            </HeaderRow>

            <Collapse
              in={isExpanded}
              timeout="auto"
              unmountOnExit
              sx={{
                minWidth: 0,
                "& .MuiCollapse-wrapper": { minWidth: 0 },
                "& .MuiCollapse-wrapperInner": { minWidth: 0, width: "100%" },
              }}
            >
              <TableContainer
                sx={{ overflowX: "auto", width: "100%", maxWidth: "100%" }}
              >
                <Table size="small" sx={{ minWidth: "max-content" }}>
                  <TableHead sx={{ borderTop: "1px solid lightgrey" }}>
                    <TableRow>
                      {BASE_HEADERS.map((header) => (
                        <TableHeadCell key={header}>{header}</TableHeadCell>
                      ))}
                      {workOrderTypes.map((itemType) => (
                        <TableHeadCell key={`wo-${itemType}`}>
                          {formatWorkOrderTypeHeader(itemType)}
                        </TableHeadCell>
                      ))}
                      <TableHeadCell>Actions</TableHeadCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const { group, workOrders } = row;
                      const groupStatusLabel = isConditionModifying
                        ? "Modifying"
                        : group.status
                        ? group.status.charAt(0).toUpperCase() +
                          group.status.slice(1)
                        : "Submitted";
                      const groupStatusPresentation =
                        getStatusPresentation(groupStatusLabel);
                      const workOrdersByType = new Map<
                        string,
                        ConditionWorkOrdersResponse
                      >();
                      workOrders.forEach((workOrder) => {
                        if (isWorkOrder(workOrder)) {
                          workOrdersByType.set(
                            workOrder.item_type ?? "",
                            workOrder
                          );
                        }
                      });
                      return (
                        <TableRow key={`${conditionId}-${group.groupId}`}>
                          <TableCell sx={{ wordBreak: "break-all" }}>
                            {group.groupId}
                          </TableCell>
                          <TableCell>
                            {formatGroupTimestamp(group.createdAt)}
                          </TableCell>
                          <TableCell>
                            {group.priority ? `P${group.priority}` : "—"}
                          </TableCell>
                          <TableCell>{group.testLocation ?? "—"}</TableCell>
                          <TableCell>
                            {formatNeedByDate(group.needByDate)}
                          </TableCell>
                          <TableCell
                            sx={{ maxWidth: 320, wordBreak: "break-word" }}
                          >
                            {group.notes ?? "—"}
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              label={groupStatusLabel}
                              colors={groupStatusPresentation}
                            />
                          </TableCell>
                          {workOrderTypes.map((itemType) => {
                            const workOrder = workOrdersByType.get(itemType);
                            if (!workOrder) {
                              return (
                                <TableCell key={`${group.groupId}-${itemType}`}>
                                  —
                                </TableCell>
                              );
                            }
                            const workOrderStatus = workOrder.status ?? "";
                            const linkLabel = getWorkOrderLinkLabel(
                              workOrder.external_link
                            );
                            return (
                              <TableCell key={`${group.groupId}-${itemType}`}>
                                <StatusBadge
                                  label={getStatusText(workOrderStatus)}
                                  colors={getStatusPresentation(
                                    workOrderStatus
                                  )}
                                  link={
                                    workOrder.external_link && linkLabel
                                      ? {
                                          href: workOrder.external_link,
                                          label: linkLabel,
                                        }
                                      : undefined
                                  }
                                />
                              </TableCell>
                            );
                          })}
                          <TableCell>
                            <Tooltip title="Cancel work order group" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Cancel work order group"
                                  onClick={() =>
                                    onDeleteSubmission(group.groupId)
                                  }
                                  disabled={
                                    isAnyConditionModifying ||
                                    isCommitLoading ||
                                    !hasActiveWorkOrder(row)
                                  }
                                  sx={{ color: "#d32f2f" }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </ConditionBlock>
        );
      })}
    </Box>
  );
};

export default CommittedConditionGroups;
